import type { Member, Root } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import { openInboxTriage, type InboxTriageAction } from "./inbox-triage-controller.ts";
import { advanceInboxTriage, inboxTriageCandidates, inboxTriageRemaining, type InboxTriageState } from "./inbox-triage.ts";

export interface InboxTriageWorkflowCapabilities {
  state(): InboxTriageState | null;
  setState(state: InboxTriageState | null): void;
  persist(state: InboxTriageState | null): Promise<void>;
  tasks(): Todo[];
  ensureTasks(): Promise<void>;
  categories(): Promise<Category[]>;
  roots(): Root[];
  members(task: Todo): Member[];
  canEdit(task: Todo): boolean;
  canMoveTo(rootId: string, categoryId: string | null): boolean;
  blockers(task: Todo): Todo[];
  performTaskAction(task: Todo, action: InboxTriageAction, value?: string): Promise<boolean>;
  openTask(task: Todo): Promise<void>;
  openQuickNotes(): void | Promise<void>;
  date(): string;
  addDays(date: string, days: number): string;
  icon(name: string): string;
  render(): Promise<void>;
  sync(): void | Promise<void>;
  now(): number;
  toast(message: string, error?: boolean): void;
}

export function createInboxTriageWorkflowController(capabilities: InboxTriageWorkflowCapabilities) {
  const candidates = () => inboxTriageCandidates(capabilities.tasks(), capabilities.canEdit);
  const save = async (state: InboxTriageState | null): Promise<void> => { await capabilities.persist(state); capabilities.setState(state); };
  const perform = async (task: Todo, action: InboxTriageAction, value?: string): Promise<boolean> => {
    if (!capabilities.canEdit(task)) return false;
    if (action !== "keep" && action !== "defer" && !await capabilities.performTaskAction(task, action, value)) return false;
    const state = capabilities.state();
    if (state) await save(advanceInboxTriage(state, task.id, capabilities.now(), action));
    return true;
  };
  return {
    remaining: () => inboxTriageRemaining(capabilities.state(), candidates()),
    save,
    perform,
    open: (restart = false) => openInboxTriage({
      tasks: capabilities.tasks, state: capabilities.state, saveState: state => save(state), ensureTasks: capabilities.ensureTasks,
      categories: capabilities.categories, roots: capabilities.roots, members: capabilities.members, canEdit: capabilities.canEdit, canMoveTo: capabilities.canMoveTo,
      blockers: capabilities.blockers, perform, openTask: capabilities.openTask, openQuickNotes: capabilities.openQuickNotes,
      date: capabilities.date, addDays: capabilities.addDays, icon: capabilities.icon, renderShell: capabilities.render,
      sync: capabilities.sync, now: capabilities.now, toast: capabilities.toast,
    }, restart),
  };
}
